<template>
    <!-- One street, two ends, no frames.

         The window IS the crossroads: a single continuous night street running
         edge to edge, with one order gathered at each end of it. There are no
         cards here and nothing has a border, because a card is a thing you
         look at and this is a place you are standing in.

         The only structural device is light, and light is also the subject.
         The warning says the order you turn away from closes to you forever,
         so the screen does that: point at one end and it steps out of the
         shadow while the other goes back into it and stays there. -->
    <div class="pl-fork" :class="litSide ? `is-lit-${litSide}` : null">
        <template v-if="isPair">
            <span class="pl-fork__crowd pl-fork__crowd--flow" :style="flowArt" aria-hidden="true"></span>
            <span class="pl-fork__crowd pl-fork__crowd--tech" :style="techArt" aria-hidden="true"></span>
            <span class="pl-fork__gloom" aria-hidden="true"></span>

            <PathPlate
                :path="flowPath"
                :chosen="modelValue === flowPath.id"
                :lit="litSide === 'flow'"
                side="flow"
                @enter="hovered = 'flow'"
                @leave="hovered = null"
                @choose="choose"
            />

            <div class="pl-fork__ask">
                <p class="pl-eyebrow">{{ t('player.creation.crossroads.eyebrow') }}</p>
                <h1 ref="heading" tabindex="-1" class="pl-fork__question">
                    {{ t('player.creation.crossroads.title') }}
                </h1>
                <div class="pl-fork__terms">
                    <p class="pl-fork__stamp">{{ t('player.creation.crossroads.permanent') }}</p>
                    <p class="pl-fork__warning">{{ t('player.creation.crossroads.warning') }}</p>
                </div>
            </div>

            <PathPlate
                :path="techPath"
                :chosen="modelValue === techPath.id"
                :lit="litSide === 'tech'"
                side="tech"
                @enter="hovered = 'tech'"
                @leave="hovered = null"
                @choose="choose"
            />
        </template>

        <!-- Fallback. The street is composed for exactly two orders standing at
             its two ends; the backend has two today but the list is an API
             response, not a constant. Any other count degrades to the ordinary
             choice grid rather than to a broken picture. -->
        <div v-else class="pl-fork__ask pl-fork__ask--alone">
            <p class="pl-eyebrow">{{ t('player.creation.crossroads.eyebrow') }}</p>
            <h1 ref="heading" tabindex="-1" class="pl-fork__question">
                {{ t('player.creation.crossroads.title') }}
            </h1>
            <div class="pl-fork__terms">
                <p class="pl-fork__stamp">{{ t('player.creation.crossroads.permanent') }}</p>
                <p class="pl-fork__warning">{{ t('player.creation.crossroads.warning') }}</p>
            </div>

            <div v-if="paths.length" class="pl-choices">
                <button
                    v-for="path in paths"
                    :key="path.id"
                    type="button"
                    class="pl-choice"
                    :class="{ 'is-on': modelValue === path.id }"
                    :aria-pressed="modelValue === path.id"
                    @click="choose(path.id)"
                >
                    <span class="pl-choice__name">{{ textOf(path).name }}</span>
                    <span class="pl-prose pl-clamp-3">{{ textOf(path).description }}</span>
                </button>
            </div>
            <p v-else class="pl-empty__hint">{{ t('player.creation.crossroads.noPaths') }}</p>
        </div>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import PathPlate from '@/components/Player/Creation/PathPlate.vue';
import {isFlowPath} from '@/components/Player/Creation/pathFlavour.js';
import {cardTextOf} from '@/components/Player/Creation/cardText.js';
import flowCrowd from '@/assets/images/backgrounds/forge/crossroads-json.webp';
import techCrowd from '@/assets/images/backgrounds/forge/crossroads-john.webp';

const props = defineProps({
    paths: {type: Array, default: () => []},
    modelValue: {type: String, default: null},
});

const emit = defineEmits(['update:modelValue']);

const {t, te} = useI18n();

function textOf(path) {
    return cardTextOf(t, te, 'path', path);
}

const hovered = ref(null);
const heading = ref(null);

const isPair = computed(() => props.paths.length === 2);

/* The two crowds are laid over the street plate the page itself carries, each
   masked into its own end of it, so the three pictures read as one photograph
   rather than as three panels. Inline because they are asset URLs — the case
   §5 rule 6 permits. */
const flowArt = {backgroundImage: `url("${flowCrowd}")`};
const techArt = {backgroundImage: `url("${techCrowd}")`};

/* Which of the two is the Flow order, and therefore which end of the street it
   is gathered at. The street plate is composed around this: its left branch is
   lit violet and its right amber. See pathFlavour.js for why this is a text
   test and not a contract. */
const flowPath = computed(() => props.paths.find(isFlowPath) ?? props.paths[0]);
const techPath = computed(() => props.paths.find((p) => p.id !== flowPath.value?.id) ?? props.paths[1]);

/* A chosen order stays lit after the pointer leaves. The street should not go
   back to dark once the player has walked to one end of it. */
const litSide = computed(() => {
    if (hovered.value) return hovered.value;
    if (props.modelValue === flowPath.value?.id) return 'flow';
    if (props.modelValue === techPath.value?.id) return 'tech';
    return null;
});

function choose(pathId) {
    emit('update:modelValue', pathId);
}

onMounted(async () => {
    await nextTick();
    heading.value?.focus();
});
</script>
